import { ArrowLeftIcon, PencilIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Center,
  Group,
  Loader,
  Modal,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CVPreviewSidebar } from '../../../components/common/CVPreviewSidebar';
import { ROUTES } from '../../../constants/routes';
import { OpportunityStatus } from '../../../models/opportunity.model';
import { OpportunityInfoPanel } from '../components/OpportunityInfoPanel';
import { OpportunityTypeBadge } from '../components/OpportunityTypeBadge';
import { PositionAddModal } from '../components/PositionAddModal';
import { PositionCard } from '../components/PositionCard';
import { STATUS_COLOR, STATUS_LABEL } from '../constants/opportunity.constants';
import { useArchiveOpportunity } from '../hooks/useOpportunityMutations';
import { useOpportunityQuery } from '../hooks/useOpportunityQuery';


export const OpportunityDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const opportunityId = id ? parseInt(id, 10) : 0;

  const { data: opportunity, isLoading, isError } = useOpportunityQuery(opportunityId);
  const archiveMutation = useArchiveOpportunity();
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [addPositionOpen, setAddPositionOpen] = useState(false);
  const [previewPersonId, setPreviewPersonId] = useState<number | null>(null);

  const handleArchive = async () => {
    try {
      await archiveMutation.mutateAsync(opportunityId);
      notifications.show({ title: 'Archived', message: 'Opportunity has been archived.', color: 'orange' });
      navigate(ROUTES.OPPORTUNITIES);
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to archive. Please try again.', color: 'red' });
    }
    setArchiveModalOpen(false);
  };

  if (isLoading) {
    return (
      <Center h={300}>
        <Loader />
      </Center>
    );
  }

  if (isError || !opportunity) {
    return (
      <Center h={300}>
        <Stack align="center" gap="xs">
          <Text c="dimmed">Opportunity not found.</Text>
          <Button variant="light" onClick={() => navigate(ROUTES.OPPORTUNITIES)}>
            Back to board
          </Button>
        </Stack>
      </Center>
    );
  }

  const isArchived = opportunity.opportunityStatus === OpportunityStatus.Archived;
  const createdBy =
    [opportunity.createdByUser?.firstName, opportunity.createdByUser?.lastName]
      .filter(Boolean)
      .join(' ') || '—';

  return (
    <>
      <Box
        style={{
          marginRight: previewPersonId !== null ? 400 : 0,
          transition: 'margin-right 0.3s ease',
        }}
      >
        <Stack
          gap="md"
          maw={1200}
          mx="auto"
          px="md"
          pb="xl"
        >
          {/* ── Sticky top bar ────────────────────────────────────────────────── */}
          <Box
            style={{
              position: 'sticky',
              top: 60,
              zIndex: 100,
              backgroundColor: 'var(--mantine-color-body)',
              borderBottom: '1px solid var(--mantine-color-default-border)',
              padding: '12px 0',
              marginBottom: 8,
            }}
          >
            <Group justify="space-between" align="center">
              <Group gap="xs">
                <ActionIcon variant="subtle" color="gray" onClick={() => navigate(ROUTES.OPPORTUNITIES)} aria-label="Back">
                  <ArrowLeftIcon style={{ width: 18, height: 18 }} />
                </ActionIcon>
                <Title order={3}>{opportunity.title}</Title>
                <Badge color={STATUS_COLOR[opportunity.opportunityStatus]} variant="light">
                  {STATUS_LABEL[opportunity.opportunityStatus]}
                </Badge>
                <OpportunityTypeBadge type={opportunity.opportunityType} />
              </Group>

              {!isArchived && (
                <Group gap="xs" style={{ flexShrink: 0 }}>
                  <Button
                    variant="light"
                    leftSection={<PencilIcon style={{ width: 14, height: 14 }} />}
                    onClick={() => navigate(ROUTES.OPPORTUNITY_EDIT(opportunity.id))}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="light"
                    color="red"
                    leftSection={<TrashIcon style={{ width: 14, height: 14 }} />}
                    onClick={() => setArchiveModalOpen(true)}
                  >
                    Archive
                  </Button>
                </Group>
              )}
            </Group>
          </Box>

          {/* Description + detail fields — inline editable */}
          <OpportunityInfoPanel opportunity={opportunity} isArchived={isArchived} createdBy={createdBy} />

          {/* Positions */}
          <Stack gap="xs">
            <Group justify="space-between" align="center">
              <Text fw={600} size="sm">
                Positions ({opportunity.positions.length})
              </Text>
              {!isArchived && (
                <Button
                  size="xs"
                  variant="light"
                  leftSection={<PlusIcon style={{ width: 13, height: 13 }} />}
                  onClick={() => setAddPositionOpen(true)}
                >
                  Add Position
                </Button>
              )}
            </Group>

            {opportunity.positions.length === 0 ? (
              <Text size="sm" c="dimmed">No positions defined yet.</Text>
            ) : (
              <Stack gap="sm">
                {opportunity.positions.map((p) => (
                  <PositionCard key={p.id} p={p} opportunityId={opportunity.id} onOpenPreview={setPreviewPersonId} isArchived={isArchived} />
                ))}
              </Stack>
            )}
          </Stack>

          {addPositionOpen && (
            <PositionAddModal
              opportunityId={opportunity.id}
              nextSortOrder={opportunity.positions.length}
              opened={addPositionOpen}
              onClose={() => setAddPositionOpen(false)}
            />
          )}

          {/* Archive confirmation */}
          <Modal
            opened={archiveModalOpen}
            onClose={() => setArchiveModalOpen(false)}
            title="Archive opportunity"
            centered
          >
            <Stack gap="md">
              <Text size="sm">
                Are you sure you want to archive <strong>{opportunity.title}</strong>?
                It will no longer appear in the board and cannot be edited.
              </Text>
              <Group justify="flex-end" gap="sm">
                <Button variant="default" onClick={() => setArchiveModalOpen(false)}>
                  Cancel
                </Button>
                <Button
                  color="red"
                  loading={archiveMutation.isPending}
                  onClick={handleArchive}
                >
                  Archive
                </Button>
              </Group>
            </Stack>
          </Modal>
        </Stack>
      </Box>

      {previewPersonId !== null && (
        <Box
          style={{
            position: 'fixed',
            top: 60,
            right: 0,
            width: 400,
            height: 'calc(100vh - 60px)',
            zIndex: 200,
            boxShadow: '-2px 0 10px rgba(0,0,0,0.1)',
          }}
        >
          <CVPreviewSidebar
            personId={previewPersonId}
            onClose={() => setPreviewPersonId(null)}
          />
        </Box>
      )}
    </>
  );
};